import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Sentry from '@sentry/react';
import { createClient, type SupabaseClient } from '@supabase/supabase-js';

import { axiosRubyClient } from '@/util/axios/axiosClient';
import { getSupabaseConfig } from '../lib/config';

interface CommsTokenResponse {
  success: boolean;
  data: {
    token: string;
    expires_at: string;
  };
}

interface SupabaseContextValue {
  client: SupabaseClient | null;
  ready: boolean;
  error: string | null;
}

const SupabaseContext = createContext<SupabaseContextValue>({
  client: null,
  ready: false,
  error: null,
});

const REFRESH_LEEWAY_MS = 60_000;
const MAX_REFRESH_INTERVAL_MS = 30 * 60 * 1000;
const RETRY_BASE_MS = 1_000;
const RETRY_CAP_MS = 5 * 60 * 1000;
const RETRY_ATTEMPTS_BEFORE_SURFACE = 5;

export function useSupabaseClient(): SupabaseClient | null {
  return useContext(SupabaseContext).client;
}

export function useSupabaseReady(): boolean {
  return useContext(SupabaseContext).ready;
}

export function useSupabaseError(): string | null {
  return useContext(SupabaseContext).error;
}

async function fetchCommsToken(): Promise<{
  token: string;
  expiresAt: number;
}> {
  const response = await axiosRubyClient.post<CommsTokenResponse>(
    '/api/admin/comms/token',
    {},
  );
  const { token, expires_at } = response.data.data;
  return { token, expiresAt: new Date(expires_at).getTime() };
}

interface SupabaseProviderProps {
  children: React.ReactNode;
  isAuthenticated: boolean;
  userId?: string | number | null;
  // The provider is inert (null client) unless mounted with `forceEnabled`.
  // Only the standalone "Supabase Chat" ops page mounts a working client;
  // everywhere else the main inbox stays on PubNub.
  forceEnabled?: boolean;
}

export function SupabaseProvider({
  children,
  isAuthenticated,
  userId,
  forceEnabled = false,
}: SupabaseProviderProps) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tokenInFlightRef = useRef<Promise<void> | null>(null);
  const retryCountRef = useRef(0);
  // Current JWT, exposed to supabase-js via the accessToken callback so
  // PostgREST + storage requests pick up the latest token without
  // having to call auth.setSession (which is a known footgun when
  // refresh_token is empty).
  const tokenRef = useRef<string | null>(null);
  // Expiry (ms epoch) of the currently applied token. Used so a transient
  // refresh failure does not tear the client down while the last-minted
  // token is still valid — mirrors the WAv2 H3 fix.
  const tokenExpiresAtRef = useRef<number>(0);
  const rotateTokenRef = useRef<() => Promise<void>>(async () => {});

  const client = useMemo<SupabaseClient | null>(() => {
    if (!forceEnabled) return null;
    const { url, publishableKey } = getSupabaseConfig();
    if (!url || !publishableKey) {
      console.warn(
        '[Supabase] Missing SUPABASE_URL / SUPABASE_PUBLISHABLE_KEY — provider disabled',
      );
      return null;
    }
    return createClient(url, publishableKey, {
      accessToken: async () => tokenRef.current ?? '',
      auth: {
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
      },
      realtime: {
        params: { eventsPerSecond: 10 },
      },
    });
    // `forceEnabled` is fixed per mount, so this only affects the initial
    // client construction — it never rebuilds the client mid-session.
  }, [forceEnabled]);

  const applyToken = useCallback(
    (token: string) => {
      tokenRef.current = token;
      if (client) client.realtime.setAuth(token);
    },
    [client],
  );

  const clearTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const scheduleRefresh = useCallback(
    (expiresAtMs: number) => {
      clearTimer();
      const delay = Math.min(
        Math.max(expiresAtMs - Date.now() - REFRESH_LEEWAY_MS, 5_000),
        MAX_REFRESH_INTERVAL_MS,
      );
      refreshTimerRef.current = setTimeout(() => {
        void rotateTokenRef.current();
      }, delay);
    },
    [clearTimer],
  );

  const scheduleBackoff = useCallback(() => {
    clearTimer();
    const attempt = retryCountRef.current;
    const delay = Math.min(
      RETRY_BASE_MS * 2 ** Math.max(attempt - 1, 0),
      RETRY_CAP_MS,
    );
    refreshTimerRef.current = setTimeout(() => {
      void rotateTokenRef.current();
    }, delay);
  }, [clearTimer]);

  const rotateToken = useCallback(async () => {
    if (!client || !isAuthenticated) return;
    if (tokenInFlightRef.current) return tokenInFlightRef.current;

    tokenInFlightRef.current = (async () => {
      try {
        const { token, expiresAt } = await fetchCommsToken();
        applyToken(token);
        tokenExpiresAtRef.current = expiresAt;
        scheduleRefresh(expiresAt);
        retryCountRef.current = 0;
        setReady(true);
        setError(null);
      } catch (err) {
        console.error('[Supabase] Token mint failed', err);
        Sentry.captureException(err, {
          tags: { feature: 'comms', action: 'mint_jwt' },
        });
        retryCountRef.current += 1;
        // Only surface the hard error / tear the client down once the
        // currently applied token has actually expired. While it is still
        // valid, retries continue silently in the background and the UI
        // stays connected (WAv2 H3 parity).
        const tokenStillValid =
          tokenExpiresAtRef.current > Date.now() + REFRESH_LEEWAY_MS;
        if (
          retryCountRef.current >= RETRY_ATTEMPTS_BEFORE_SURFACE &&
          !tokenStillValid
        ) {
          setReady(false);
          setError('Unable to connect to chat. Refresh the page to retry.');
        }
        scheduleBackoff();
      } finally {
        tokenInFlightRef.current = null;
      }
    })();
    return tokenInFlightRef.current;
  }, [client, isAuthenticated, applyToken, scheduleRefresh, scheduleBackoff]);

  useEffect(() => {
    rotateTokenRef.current = rotateToken;
  }, [rotateToken]);

  // Main token lifecycle — fires the first mint and tears down only on
  // auth/userId changes. The refresh timer is owned here.
  useEffect(() => {
    if (!client || !isAuthenticated || !userId) {
      tokenRef.current = null;
      tokenExpiresAtRef.current = 0;
      setReady(false);
      return;
    }
    retryCountRef.current = 0;
    void rotateToken();
    return () => {
      clearTimer();
    };
  }, [client, isAuthenticated, userId, rotateToken, clearTimer]);

  // Visibility listener: re-mint when the tab returns to foreground.
  // Keyed only on `client` so this listener does NOT re-register on
  // every rotateToken closure rebuild — which was clearing the refresh
  // timer mid-cycle in the previous implementation.
  useEffect(() => {
    if (!client) return;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void rotateTokenRef.current();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [client]);

  // Logout cleanup: clear realtime auth, tear down any open channels,
  // drop the cached token.
  useEffect(() => {
    if (!client) return;
    if (!isAuthenticated) {
      client.realtime.setAuth('');
      try {
        client.removeAllChannels();
      } catch (_) {
        /* ignore — best-effort cleanup */
      }
      tokenRef.current = null;
      tokenExpiresAtRef.current = 0;
      setReady(false);
    }
  }, [client, isAuthenticated]);

  const value = useMemo<SupabaseContextValue>(
    () => ({ client, ready, error }),
    [client, ready, error],
  );

  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  );
}
